// SAFE-WRITE-BEGIN: _EmailDocument.tsx
/**
 * ADD_VAL-REPORT-EMAIL-1 - "Email document" beside Produce / Download on the Finalize screen.
 *
 * Shown only once a document for the live version is archived, and only to the roles the
 * server lets send (an owner or another firm's signer gets a 403/404 and sees nothing).
 * The server picks the recipient; this shows it masked and asks before sending.
 * VAL-EMAIL-ENTRY-1: with no client email on file the sender types one ("Client email"); it is
 * shown in full in the confirm step and saved on the engagement by the server.
 */
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { fetchDocumentEmail, sendDocumentEmail, type DocumentEmailInfo } from '@/api/valuationEmail'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatMediumDate } from '@/utils/date'
import { isValidEmail } from '@/utils/email'


const HELP = "No client email on file. Enter the client's email to send the PDF. It is saved on this engagement."
const INVALID = 'Enter one valid email address.'

export function EmailDocumentButton({
  engagementId,
  liveVersion,
  documentsOnFile,
  showStatus = true,
}: {
  engagementId: string
  liveVersion: number | null
  documentsOnFile: boolean
  /** The documents card shows "Emailed <date>"; the action bar does not. */
  showStatus?: boolean
}) {
  const [info, setInfo] = useState<DocumentEmailInfo | null>(null)
  const [open, setOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [typed, setTyped] = useState('')
  const [changing, setChanging] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [invalid, setInvalid] = useState(false)

  const load = useCallback(() => {
    if (!engagementId || !documentsOnFile) { setInfo(null); return }
    fetchDocumentEmail(engagementId).then(setInfo).catch(() => setInfo(null))
  }, [engagementId, documentsOnFile, liveVersion])

  useEffect(load, [load])

  if (!documentsOnFile || !info || !info.archived) return null

  const asking = !!info.needs_email || changing
  const address = typed.trim().toLowerCase()

  function reset(next: boolean) {
    setOpen(next)
    if (!next) { setTyped(''); setChanging(false); setConfirming(false); setInvalid(false) }
  }

  function check() {
    if (!isValidEmail(typed)) { setInvalid(true); return }
    setInvalid(false)
    setConfirming(true)
  }

  async function send() {
    setSending(true)
    try {
      const res = asking ? await sendDocumentEmail(engagementId, address) : await sendDocumentEmail(engagementId)
      toast.success(res.message)
      reset(false)
      load()
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'The email could not be sent.')
      reset(false)
      load()
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-1">
      <Button variant="outline" className="w-full" disabled={!info.can_email} onClick={() => reset(true)}>
        Email document
      </Button>
      {!info.can_email && info.reason && <p className="text-xs text-amber-800">{info.reason}</p>}
      {showStatus && info.emailed_on && (
        <p className="text-xs text-gray-600">Emailed {formatMediumDate(info.emailed_on)}</p>
      )}
      <Dialog open={open} onOpenChange={reset}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email the document?</DialogTitle>
            <DialogDescription>
              {asking && confirming
                ? `Send the PDF to ${address}. It will be saved as this engagement's client email.`
                : asking
                  ? 'Send the PDF of the signed record.'
                  : `Send the PDF of the signed record to ${info.to_masked}.`}
            </DialogDescription>
          </DialogHeader>
          {asking && !confirming && (
            <div className="space-y-1">
              <Label htmlFor="client-email">Client email</Label>
              <Input
                id="client-email"
                type="email"
                value={typed}
                onChange={e => { setTyped(e.target.value); setInvalid(false) }}
              />
              {invalid
                ? <p className="text-xs text-red-700">{INVALID}</p>
                : <p className="text-xs text-gray-600">{info.needs_email ? HELP : 'It replaces the client email saved on this engagement.'}</p>}
            </div>
          )}
          {!asking && info.to_masked && (
            <button type="button" className="text-xs text-blue-700 underline" onClick={() => setChanging(true)}>
              Change
            </button>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => (confirming ? setConfirming(false) : reset(false))} disabled={sending}>
              {confirming ? 'Back' : 'Cancel'}
            </Button>
            {asking && !confirming
              ? <Button onClick={check}>Continue</Button>
              : <Button onClick={send} disabled={sending}>{sending ? 'Sending…' : 'Send'}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
// SAFE-WRITE-EOF: _EmailDocument.tsx
